import { useEffect, useRef } from 'react';

import styled from 'styled-components';

import { Checkbox } from '@components/checkbox';
import { createForm, useFormField } from '@components/form';
import { IconDocument } from '@components/icons/document';
import { IconDownload } from '@components/icons/download';
import { IconFolder } from '@components/icons/folder';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { InfoBox } from '@components/info-box';
import { RadioGroup } from '@components/radio-group';
import { SelectMulti, SelectOption } from '@components/selectmulti';
import { CloseButton, hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { State, StateGroup } from '@components/state-selector/state-group';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useExportApplicants } from 'api/jobs/hooks';
import {
  ApplicantFolderNamingTypes,
  ExportApplicantsData,
  ExportFieldsType,
} from 'api/jobs/requests';
import { useAuthenticatedUser } from 'features/authentication';
import { useStore } from 'models/Provider';
import { IJob, IJobFormQuestion, IJobFormSection, IJobLanguage } from 'models/types';

import { useGatheringExportDataModal } from '../modals/gathering-export-data-modal';

export const useExportApplicantsSidebar = () => {
  const sidebar = useSidebar(ExportApplicantsSidebar, {
    variant: 'CONTENT_HEAVY',
    closeOnClickOutside: true,
  });
  return sidebar;
};

type ExportApplicantsSidebarProps = {
  job: IJob;
  applicationIds: number[];
};

type ExportApplicantsTypes = 'spreadsheet' | 'compressed';
type ExportApplicantsStateItem = State & { id: ExportApplicantsTypes };

type ExportApplicantsFormData = {
  exportType: ExportApplicantsTypes;
  xlsxOrCsv: 'xlsx' | 'csv';
  folderNaming?: ApplicantFolderNamingTypes[];
  headers?: ExportFieldsType[];
  data?: ExportFieldsType[];
};

const Form = createForm<ExportApplicantsFormData>();

type CheckboxGroupData = {
  groupKey: string;
  groupLabel: string;
  options: { value: string; label: string }[];
};

type GroupedCheckboxesProps = {
  groups: CheckboxGroupData[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
};

const GroupedCheckboxes = ({ groups, selectedValues, onChange }: GroupedCheckboxesProps) => {
  const getGroupSelectionState = (groupOptions: { value: string; label: string }[]) => {
    const groupValues = groupOptions.map((option) => option.value).filter((value) => value !== '');
    const isAllSelected =
      groupValues.length > 0 && groupValues.every((value) => selectedValues.includes(value));
    const someSelected = groupValues.some((value) => selectedValues.includes(value));

    return { groupValues, isAllSelected, someSelected };
  };

  const handleGroupToggle = (groupOptions: { value: string; label: string }[]) => {
    const { groupValues, isAllSelected } = getGroupSelectionState(groupOptions);

    if (isAllSelected) {
      onChange(selectedValues.filter((value) => !groupValues.includes(value)));
    } else {
      const newValues = [...selectedValues.filter((value) => !groupValues.includes(value))];
      newValues.push(...groupValues);
      onChange(newValues);
    }
  };

  const handleIndividualToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <Stack space={4}>
      {groups.map((group) => {
        const { isAllSelected, someSelected } = getGroupSelectionState(group.options);

        return (
          <GroupContainer key={group.groupKey}>
            <Checkbox
              value={isAllSelected ? true : someSelected ? 'mixed' : false}
              label={group.groupLabel}
              onChange={() => handleGroupToggle(group.options)}
              labelFont="semibold14"
            />
            <Stack space={1}>
              {group.options.map((option) =>
                option.value === '' ? (
                  <Text
                    key={option.value}
                    text={option.label}
                    font="regular14"
                    color="textHelper"
                  />
                ) : (
                  <Checkbox
                    key={option.value}
                    value={selectedValues.includes(option.value)}
                    label={option.label}
                    onChange={() => handleIndividualToggle(option.value)}
                    labelFont="regular14"
                  />
                )
              )}
            </Stack>
          </GroupContainer>
        );
      })}
    </Stack>
  );
};

const ExportApplicantsSidebar = (props: ExportApplicantsSidebarProps) => {
  const { t } = useScopedTranslate(SelectSidebarLocale);
  const { job, applicationIds } = props;
  const exportApplicants = useExportApplicants();
  const showGatheringDataModal = useGatheringExportDataModal();
  const { current } = useStore();
  const { useSsnRegistry } = current.info;

  let availableQuestions: Record<string, { label: string; checked: boolean }> = {};
  let defaultLanguage =
    job.languages.find(
      (lang: IJobLanguage) => lang.isEnabled && lang.language === current.info.defaultLanguage
    ) ?? null;

  if (!defaultLanguage && job.languages.length > 0) {
    defaultLanguage =
      job.languages.find((lang: IJobLanguage) => lang.isEnabled) ?? job.languages[0];
  }

  defaultLanguage?.form?.sections.map((s: IJobFormSection) =>
    s.questions.forEach((q: IJobFormQuestion) => {
      if (q.type !== 'attachment') availableQuestions[q.id] = { label: q.title, checked: false };
    })
  );

  const totalApplications = job.numApplications;

  const folderNamingOptions: SelectOption[] = [
    {
      value: 'application_name',
      label: t('applicationFullName'),
    },
    {
      value: 'application_ssn',
      label: t('applicationSSN'),
    },
    {
      value: 'job_name',
      label: t('jobName'),
    },
    {
      value: 'application_job_stage',
      label: t('stageName'),
    },
    ...(useSsnRegistry
      ? [
          {
            value: 'national_registry_name',
            label: t('nationalRegistryFullName'),
          },
          {
            value: 'national_registry_ssn',
            label: t('nationalRegistrySSN'),
          },
        ]
      : []),
  ];

  const handleSubmit = async (data: ExportApplicantsFormData) => {
    if (data.exportType === 'spreadsheet') {
      const { folderNaming, ...rest } = data;
      const exportData: ExportApplicantsData = {
        //@ts-ignore
        jobId: job.id,
        exportType: data.xlsxOrCsv,
        fields: rest.headers || [],
        applicationIds,
      };
      exportApplicants.mutateAsync(exportData);
    } else if (data.exportType === 'compressed') {
      const exportData: ExportApplicantsData = {
        //@ts-ignore
        jobId: job.id,
        exportType: 'zip',
        fields: data.data || [],
        folderNaming: data.folderNaming || [],
        applicationIds,
      };
      exportApplicants.mutateAsync(exportData);
    }
    hideSidebar();
    showGatheringDataModal();
  };

  return (
    <Sidebar>
      <Sidebar.Header>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            <IconDownload color="elementBlue100" />
            <Sidebar.HeaderTitle title={t('exportApplicants')} />
          </Stack>
          <Stack space={2} orientation="horizontal" align="center">
            <CloseButton />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Form onSubmit={handleSubmit}>
          <ExportApplicantsFormContent
            folderNamingOptions={folderNamingOptions}
            numSelectedApplicants={applicationIds?.length ?? 0}
            totalApplications={totalApplications}
            availableQuestions={availableQuestions}
          />
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const ExportApplicantsFormContent = ({
  folderNamingOptions,
  numSelectedApplicants,
  totalApplications,
  availableQuestions,
}: {
  folderNamingOptions: SelectOption[];
  numSelectedApplicants: number;
  totalApplications: number;
  availableQuestions: Record<string, { label: string; checked: boolean }>;
}) => {
  const { t } = useScopedTranslate(SelectSidebarLocale);
  const me = useAuthenticatedUser();
  const exportTypeProps = useFormField({
    id: 'exportType',
    label: 'Export type',
    defaultValue: 'spreadsheet',
  });
  const xlsxOrCsvProps = useFormField({
    id: 'xlsxOrCsv',
    label: t('xlsxOrCsv'),
    defaultValue: 'xlsx',
  });

  const folderNamingProps = useFormField<ApplicantFolderNamingTypes[]>({
    id: 'folderNaming',
    label: t('applicantFolderNaming'),
    defaultValue: ['application_name'],
  });

  const headersProps = useFormField<string[]>({
    id: 'headers',
    label: t('headers'),
    defaultValue: [],
  });

  const dataProps = useFormField<string[]>({
    id: 'data',
    label: t('data'),
    defaultValue: ['cv', 'cover_letter', 'application_question_responses'],
  });

  const chosenExportType = exportTypeProps.value;
  const previousExportType = useRef<ExportApplicantsTypes | undefined>(undefined);

  useEffect(() => {
    if (
      previousExportType.current !== undefined &&
      previousExportType.current !== chosenExportType
    ) {
      if (chosenExportType === 'spreadsheet') {
        dataProps.onChange([]);
        headersProps.onChange([]);
      } else if (chosenExportType === 'compressed') {
        headersProps.onChange([]);
        dataProps.onChange(['cv', 'cover_letter', 'application_question_responses']);
      }
    }
    previousExportType.current = chosenExportType as ExportApplicantsTypes;
  }, [chosenExportType, headersProps, dataProps]);

  const questionOptions = Object.entries(availableQuestions).map(([id, question]) => ({
    value: id,
    label: question.label,
  }));

  const headerGroups = [
    {
      groupKey: 'applicantDetails',
      groupLabel: t('applicantDetails'),
      options: [
        { value: 'name', label: t('name') },
        { value: 'email', label: t('email') },
        { value: 'phone', label: t('phone') },
        { value: 'ssn', label: t('ssn') },
      ],
    },
    {
      groupKey: 'applicantJobStatus',
      groupLabel: t('applicantJobStatus'),
      options: [
        { value: 'job_stage_name', label: t('stageName') },
        { value: 'current_state', label: t('currentState') },
        { value: 'num_reviews', label: t('numberOfReviews') },
        { value: 'num_notes', label: t('numberOfNotes') },
        { value: 'source', label: t('source') },
      ],
    },
    {
      groupKey: 'dates',
      groupLabel: t('dates'),
      options: [
        { value: 'date_applied', label: t('dateApplied') },
        { value: 'date_of_hire', label: t('dateOfHire') },
        { value: 'date_of_rejection', label: t('dateOfRejection') },
      ],
    },
    {
      groupKey: 'applicationQuestionAnswers',
      groupLabel: t('applicationQuestionAnswers'),
      options:
        questionOptions.length > 0
          ? [...questionOptions]
          : [{ value: '', label: t('noQuestionResponses') }],
    },
  ];

  const dataGroups = [
    {
      groupKey: 'applicantDataToExport',
      groupLabel: t('applicantDataToExport'),
      options: [
        { value: 'cv', label: t('cv') },
        { value: 'cover_letter', label: t('coverLetter') },
        { value: 'application_question_responses', label: t('applicationQuestionResponses') },
        { value: 'team_notes', label: t('teamNotes') },
        ...(me.role === 'admin' || me.role === 'superadmin'
          ? [{ value: 'admin_notes', label: t('adminNotes') }]
          : []),
        { value: 'message_history', label: t('messageHistory') },
        { value: 'files', label: t('files') },
      ],
    },
  ];

  return (
    <>
      <Stack space={6}>
        {numSelectedApplicants > 0 && (
          <InfoBox
            text={t('activeSelectionApplied', {
              selected_number: numSelectedApplicants,
              total_number: totalApplications,
            })}
            variant="indigo"
            icon={<IconFormIndicatorInfo color="elementIndigo100" />}
          />
        )}
        <Sidebar.Separator />
        <Container>
          <Text text={t('exportType')} font="semibold14" />
          <Stack space={4}>
            <StateGroup
              orientation="horizontal"
              selectedStateId={'spreadsheet'}
              onStateSelected={(state) => {
                exportTypeProps.onChange(state.id);
              }}
              stateItems={
                [
                  {
                    id: 'spreadsheet',
                    orientation: 'horizontal',
                    icon: <IconDocument />,
                    label: t('spreadsheet'),
                    description: t('xlsxOrCsv'),
                  },
                  {
                    id: 'compressed',
                    orientation: 'horizontal',
                    icon: <IconFolder />,
                    label: t('compressed'),
                    description: t('zip'),
                  },
                ] as ExportApplicantsStateItem[]
              }
            />
            <Form.ConditionalValue id="exportType" when={(value) => value === 'spreadsheet'}>
              <Form.RadioGroup
                id="xlsxOrCsv"
                label={t('xlsxOrCsv')}
                defaultValue={xlsxOrCsvProps.value}
              >
                <RadioGroup.Option label={t('xlsx')} value="xlsx" />
                <RadioGroup.Option label={t('csv')} value="csv" />
              </Form.RadioGroup>
            </Form.ConditionalValue>
            <Form.ConditionalValue id="exportType" when={(value) => value === 'compressed'}>
              <SelectMulti
                label={folderNamingProps.label}
                options={folderNamingOptions}
                value={folderNamingOptions.filter((option) =>
                  folderNamingProps.value?.includes(option.value as ApplicantFolderNamingTypes)
                )}
                onChange={(selectedOptions) => {
                  folderNamingProps.onChange(
                    selectedOptions.map((option) => option.value as ApplicantFolderNamingTypes)
                  );
                }}
                onBlur={folderNamingProps.onBlur}
                isDisabled={folderNamingProps.isDisabled}
                error={folderNamingProps.error}
              />
              <Text text={t('folderNamingHelpText')} font="regular14" color="textHelper" />
            </Form.ConditionalValue>
          </Stack>
        </Container>
        <Sidebar.Separator />
        <Container>
          <Text
            text={chosenExportType === 'spreadsheet' ? t('headers') : t('data')}
            font="semibold14"
          />
          <Form.ConditionalValue id="exportType" when={(value) => value === 'spreadsheet'}>
            <GroupedCheckboxes
              groups={headerGroups}
              selectedValues={headersProps.value ?? []}
              onChange={headersProps.onChange}
            />
          </Form.ConditionalValue>
          <Form.ConditionalValue id="exportType" when={(value) => value === 'compressed'}>
            <GroupedCheckboxes
              groups={dataGroups}
              selectedValues={dataProps.value ?? []}
              onChange={dataProps.onChange}
            />
          </Form.ConditionalValue>
        </Container>
      </Stack>
      <Sidebar.Footer>
        <Form.Actions>
          <Form.Reset text={t('core.cancel')} onClick={hideSidebar} />
          <Form.Submit
            text={t('export')}
            shouldDisableWhenNotDirty
            variant="primary-blue"
            iconLeft={<IconDownload />}
            isDisabled={
              (chosenExportType === 'spreadsheet' &&
                (!headersProps.value || headersProps.value.length === 0)) ||
              (chosenExportType === 'compressed' &&
                (!dataProps.value || dataProps.value.length === 0))
            }
          />
        </Form.Actions>
      </Sidebar.Footer>
    </>
  );
};

const Container = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  '>div:nth-child(2)': {
    width: pxToRem(424),
  },
});

const GroupContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(8),
});

const SelectSidebarLocale = {
  en: {
    exportApplicants: 'Export applicants',
    activeSelectionApplied:
      'Active filters/selection applied • Exporting {selected_number} of {total_number} applicants',
    spreadsheet: 'Spreadsheet',
    xlsxOrCsv: 'XLSX or CSV',
    compressed: 'Compressed folders',
    zip: 'ZIP',
    xlsx: 'XLSX',
    csv: 'CSV',
    applicantFolderNaming: 'Applicant Folder Naming',
    folderNamingHelpText:
      'Specify the naming convention for each applicant folder in the exported ZIP file.\n\nFolders will use hyphens (-) as separators, with the applicant ID added at the end to ensure uniqueness\n(e.g. name-job-12345)',
    headers: 'Headers',
    data: 'Data',
    exportType: 'Export Type',
    export: 'Export',
    applicationFullName: 'Application Full Name',
    applicationSSN: 'Application SSN',
    jobName: 'Job Name',
    stageName: 'Stage Name',
    nationalRegistryFullName: 'National Registry Full Name',
    nationalRegistrySSN: 'National Registry SSN',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    ssn: 'SSN / ID Number',
    currentState: 'Current State',
    numberOfReviews: 'Number of Reviews',
    numberOfNotes: 'Number of Notes',
    source: 'Source',
    dateApplied: 'Date Applied',
    dateOfHire: 'Date of Hire',
    dateOfRejection: 'Date of Rejection',
    applicationQuestionResponses: 'Application Question Responses',
    applicantDetails: 'Applicant Details',
    applicantJobStatus: 'Applicant Job Status',
    dates: 'Dates',
    applicationQuestionAnswers: 'Application Question Answers',
    applicantDataToExport: 'Applicant data to export',
    cv: 'CV',
    coverLetter: 'Cover Letter',
    teamNotes: 'Team Notes',
    adminNotes: 'Admin Notes',
    messageHistory: 'Message History',
    files: 'Files',
    noQuestionResponses: 'No question responses',
  },
  is: {
    exportApplicants: 'Flytja út umsækjendur',
    activeSelectionApplied:
      'Virk síun/val í notkun • Flyt út {selected_number} af {total_number} umsækjendum',
    spreadsheet: 'Töflureiknir',
    xlsxOrCsv: 'XLSX eða CSV',
    compressed: 'Þjappaðar möppur',
    zip: 'ZIP',
    xlsx: 'XLSX',
    csv: 'CSV',
    applicantFolderNaming: 'Nafngift möppu umsækjanda',
    folderNamingHelpText: 'Tilgreindu nafngift hverrar möppu umsækjanda í útfluttri ZIP-skrá',
    headers: 'Dálkar',
    data: 'Gögn',
    exportType: 'Tegund útflutnings',
    export: 'Flytja út',
    applicationFullName: 'Fullt nafn umsækjanda',
    applicationSSN: 'Kennitala umsækjanda',
    jobName: 'Heiti starfs',
    stageName: 'Heiti dálks',
    nationalRegistryFullName: 'Fullt nafn úr þjóðskrá',
    nationalRegistrySSN: 'Kennitala úr þjóðskrá',
    name: 'Nafn',
    email: 'Netfang',
    phone: 'Símanúmer',
    ssn: 'Kennitala',
    currentState: 'Núverandi dálkur',
    numberOfReviews: 'Fjöldi yfirferða',
    numberOfNotes: 'Fjöldi athugasemda',
    source: 'Merki',
    dateApplied: 'Dagsetning umsóknar',
    dateOfHire: 'Dagsetning ráðningar',
    dateOfRejection: 'Dagsetning höfnunar',
    applicationQuestionResponses: 'Svör við spurningum í umsókn',
    applicantDetails: 'Upplýsingar um umsækjanda',
    applicantJobStatus: 'Staða umsækjanda í ráðningarferli',
    dates: 'Dagsetningar',
    applicationQuestionAnswers: 'Svör við spurningum í umsókn',
    applicantDataToExport: 'Gögn umsækjanda til útflutnings',
    cv: 'Ferilskrá',
    coverLetter: 'Kynningarbréf',
    teamNotes: 'Umsagnir teymis',
    adminNotes: 'Athugasemdir stjórnenda',
    messageHistory: 'Skilaboðasaga',
    files: 'Skrár',
    noQuestionResponses: 'Engin svör við spurningum',
  },
};
